import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, Zap, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CleaningOptionsComparisonProps {
  series?: 'BIG' | 'BIG-S' | 'GNG';
}

const CleaningOptionsComparison: React.FC<CleaningOptionsComparisonProps> = () => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="mb-20"
      id="cleaning-options"
    >
      <Link to="/filtration/inline/cleaning">
        <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center hover:text-ctc-blue transition-colors cursor-pointer">
          Cleaning Options
        </h2>
      </Link>
        <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
          Choose the cleaning method that best fits your operation, water quality, and maintenance preferences.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          <Link to="/filtration/inline/cleaning/manual">
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-ctc-blue transition-colors cursor-pointer h-full"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6 mx-auto">
                <Wrench className="text-ctc-blue" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Manual Cleaning</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Lowest upfront cost</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Simple operation with no power requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Requires shutdown and manual screen removal</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Best for low solids and infrequent cleaning needs</span>
                </li>
              </ul>
            </motion.div>
          </Link>

          <Link to="/filtration/inline/cleaning/semi-auto">
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-ctc-blue cursor-pointer h-full"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-6 mx-auto">
                <Settings className="text-orange-600" size={32} />
              </div>
              <div className="bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full w-fit mx-auto mb-4">
                RECOMMENDED
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Semi-Auto (BIF/MVC)</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Brush-In-Filter or manual vacuum/brush combo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Reduces downtime significantly</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Minimal power requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Ideal balance of cost and convenience</span>
                </li>
              </ul>
            </motion.div>
          </Link>

          <Link to="/filtration/inline/cleaning/automatic">
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-green-600 transition-colors cursor-pointer h-full"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6 mx-auto">
                <Zap className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Fully Automatic (RAB/AVC)</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Self-cleaning brush or vacuum/brush system</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Continuous service without interruption</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Ideal for high solids and remote locations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ctc-blue mr-2">•</span>
                  <span>Automated operation with minimal oversight</span>
                </li>
              </ul>
            </motion.div>
          </Link>
        </div>
      </motion.section>
  );
};

export default CleaningOptionsComparison;
